import { Job } from "@/lib/schema";

export interface AvailableJobsFilters {
  category: string[];
  employmentType: string[];
  skills: string[];
}

// Common department acronyms that should always be uppercase (duplicated from textUtils for consistency)
const DEPARTMENT_ACRONYMS = [
  'hr', 'it', 'pm', 'qa', 'qc', 'ui', 'ux', 'ai', 'ml', 'api', 
  'cto', 'ceo', 'cfo', 'coo', 'vp', 'r&d', 'rd', 'pr', 'seo', 
  'crm', 'erp', 'bi', 'devops', 'ops', 'ba', 'dba', 'admin', 'support', 'sales'
];

// @deprecated Use formatDisplayText from @/lib/textUtils instead for consistent formatting
export const formatCategoryName = (category: string): string => {
  return category
    .split(/[-_\s]+/) // Split on hyphens, underscores, or spaces
    .map(word => {
      const lowerWord = word.toLowerCase();
      
      // Check if this word is a department acronym
      if (DEPARTMENT_ACRONYMS.includes(lowerWord)) {
        return word.toUpperCase();
      }
      
      // Regular title case formatting
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

// Calculate days ago from a date, handling Firebase timestamps and timezone
export const calculateDaysAgo = (date: Date | string | any): number => {
  let targetDate: Date;
  
  // Handle Firebase timestamp or various date formats
  if (date && typeof date === 'object' && date.toDate) {
    // Firebase Timestamp object
    targetDate = date.toDate();
  } else if (typeof date === 'string') {
    // String date
    targetDate = new Date(date);
  } else if (date instanceof Date) {
    // Already a Date object
    targetDate = date;
  } else {
    // Fallback for unknown formats
    targetDate = new Date(date);
  }
  
  // Get current date in user's timezone (start of today)
  const now = new Date();
  const currentDateStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  // Get target date in user's timezone (start of that day)
  const targetDateStart = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
  
  // Calculate difference in days
  const diffInMs = currentDateStart.getTime() - targetDateStart.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  
  // If less than 24 hours difference or same day, return 1
  // Otherwise return the actual number of days
  return diffInDays <= 0 ? 1 : diffInDays;
};

// Check if any filters are active
export const hasActiveFilters = (
  searchQuery: string,
  locationFilter: string,
  filters: AvailableJobsFilters
): boolean => {
  return searchQuery !== "" || 
         locationFilter !== "all" || 
         filters.category.length > 0 ||
         filters.employmentType.length > 0 ||
         filters.skills.length > 0;
};

// Filter jobs based on filters state
export const filterJobsByFilters = (
  jobs: Job[],
  filters: AvailableJobsFilters
): Job[] => {
  return jobs.filter((job: Job) => {
    // Filter by category
    if (filters.category.length > 0 && !filters.category.includes(job.category)) {
      return false;
    }
    
    // Filter by employment type
    if (filters.employmentType.length > 0 && !filters.employmentType.includes(job.employmentType)) {
      return false;
    }
    
    // Filter by skills (if job has any of the selected skills)
    if (filters.skills.length > 0) {
      const hasMatchingSkill = filters.skills.some(skill => 
        job.skills.some(jobSkill => 
          jobSkill.toLowerCase().includes(skill.toLowerCase())
        )
      );
      if (!hasMatchingSkill) {
        return false;
      }
    }
    
    return true;
  });
};

// Generate unique locations from jobs data
export const extractUniqueLocations = (jobs: Job[]): string[] => {
  return Array.from(new Set(jobs.map(job => job.location).filter(Boolean)));
};

// Create empty filters object
export const createEmptyFilters = (): AvailableJobsFilters => ({
  category: [],
  employmentType: [],
  skills: [],
});
